import { NativeImage } from "@/components/native/NativeImage";
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { ordersLabels } from "@/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CarDetailedInfo } from "./CarDetailedInfo";
import { ChevronUp, Truck, ExternalLink, Info, FileSignature } from "lucide-react";
import { LicensePlate } from "../my-auctions/LicensePlate";
import { SaleTypeBadge } from "@/components/ui/SaleTypeBadge";
import { Order } from "@/types/order";
import { Transport } from "@/types/transport";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface CarCardsProps {
  orders: Order[];
  selectedCar: string | null;
  setSelectedCar: (id: string | null) => void;
}


export const CarCards = ({
  orders,
  selectedCar,
  setSelectedCar,
}: CarCardsProps) => {
  const { language } = useLanguage();
  const o = ordersLabels(language);
  const navigate = useNavigate();

  const getDeliveryStatus = (transport: Transport | null | undefined): string | undefined => {
    if (!transport?.status) return o.deliveryStatus?.unknown;
    switch (transport.status) {
      case "completed":
        return o.deliveryStatus?.delivered;
      case "in_progress":
        return o.deliveryStatus?.inProgress;
      case "planned":
        return o.deliveryStatus?.confirmed;
      case "requested":
        return o.deliveryStatus?.requestSent;
      case "not_started":
        return o.deliveryStatus?.notBooked;
      default:
        return o.deliveryStatus?.unknown;
    }
  };

  const getSignatureStatusInfo = (
    signatureStatus?: "not_requested" | "requested" | "signed" | "declined",
  ) => {
    switch (signatureStatus) {
      case "signed":
        return {
          text: o.contractPopup?.signatureSigned ?? "Signerat",
          color: "bg-green-100 text-green-800 border-green-200",
          requiresAction: false,
        };
      case "requested":
        return {
          text: o.contractPopup?.signatureRequested ?? "Signering begard",
          color: "bg-amber-100 text-amber-800 border-amber-200",
          requiresAction: true,
        };
      case "declined":
        return {
          text: "Signering misslyckad",
          color: "bg-red-100 text-red-800 border-red-200",
          requiresAction: true,
        };
      default:
        return {
          text: o.contractPopup?.signatureNotRequested ?? "Ingen signering begard",
          color: "bg-gray-100 text-gray-700 border-gray-200",
          requiresAction: false,
        };
    }
  };

  const handleNavigateToListing = useCallback(
    (listingId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      // Find the order to get campaignId
      const order = orders.find((o) => o.listing.id === listingId);
      if (order && order.listing.campaignId) {
        navigate(`/auctions/${order.listing.campaignId}/${listingId}`, {
          state: { from: "/orders" },
        });
      }
    },
    [navigate, orders]
  );

  const handleCardClick = useCallback(
    (listingId: string) => {
      const newSelected = selectedCar === listingId ? null : listingId;
      setSelectedCar(newSelected);
    },
    [selectedCar, setSelectedCar]
  );

  if (orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">{o.card.noOrdersToShow}</div>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4 w-full">
      {orders.map((order, index) => {
        const listing = order.listing;
        const payment = order.listing.buyer_payment;

        const transport = order.listing.buyer_transport;
        const signatureStatus =
          payment.contract?.buyer_signature_status ?? "not_requested";
        const signatureStatusInfo = getSignatureStatusInfo(signatureStatus);
        const signedByName = payment.contract?.buyer_signed_by_name;

        // Calculate delivery status
        const deliveryStatus = getDeliveryStatus(transport);

        // TODO: nu är ju status "köpt" på alla. Ska vi inte göra så att vi lägger till en flik typ "Dina pågående budgivningar" typ? Så läser vi in alla listings (autkioner)
        // där personen är en av budarna? Är den såld (och någon annan köpt den - i.e. winnerId) - kan ha en knapp typ "Ta bort från min lista" eller så?
        const status = listing.status === "completed" ? o.card.purchased : o.card.reserved;
        const isCompletedOrder = listing.status === "completed";
        const updatedLabel = listing.updatedAt
          ? new Date(listing.updatedAt).toLocaleString("sv-SE", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          : o.card.unknown;

        // Payment status with colors
        const getPaymentStatus = (status: string) => {
          switch (status) {
            case "paid":
              return {
                text: o.invoice.paid,
                color: "bg-green-100 text-green-800 border-green-200",
              };
            case "pending":
              return {
                text: o.invoice.pending,
                color: "bg-orange-100 text-orange-800 border-orange-200",
              };
            case "unpaid":
            case "failed":
              return {
                text: o.invoice.unpaid,
                color: "bg-red-100 text-red-800 border-red-200",
              };
            case "overdue":
              return {
                text: o.invoice.overdue,
                color: "bg-red-200 text-red-900 border-red-300 font-bold",
              };
            case "cancelled":
              return {
                text: o.invoice.cancelled,
                color: "bg-red-100 text-red-800 border-red-200",
              };
            default:
              return {
                text: o.invoice.unpaid,
                color: "bg-red-100 text-red-800 border-red-200",
              };
          }
        };

        const paymentStatusInfo = getPaymentStatus(payment.status);

        // Check if payment is overdue for special styling
        const isOverdue = payment.status === "overdue";

        // Calculate price breakdown
        const isBuyNowTriggered = listing.auctionDetails?.isBuyNowTriggered === true;
        const auctionPrice = isBuyNowTriggered
          ? (listing.auctionDetails?.currentBuyNowPrice ?? listing.auctionDetails?.buyNowFixed)
          : listing.auctionDetails?.currentBid;
        const listingComputedTotal =
          auctionPrice || listing.directPurchaseDetails?.currentPrice || 0;

        // Source of truth for what the buyer should pay is buyer_payment.amount (admin-adjustable).
        // Fallback to listing price if payment amount is missing.
        const paymentTotal =
          typeof payment.amount === "number" && payment.amount > 0
            ? payment.amount
            : null;

        const totalPrice = paymentTotal ?? listingComputedTotal;

        const rawBidFee =
          (listing.auctionDetails as any)?.bidFee ||
          (listing.directPurchaseDetails as any)?.bidFee ||
          0;
        const bidFee = Math.max(0, Number(rawBidFee) || 0);
        const basePrice = Math.max(0, totalPrice - bidFee);
        const hasBidFee = bidFee > 0;

        const price = `${totalPrice.toLocaleString("sv-SE")} kr`;

        return (
          <div key={`${listing.id}-${index}`} className="w-full">
            <Card
              className={`glass-card w-full max-w-full overflow-hidden ${
                isOverdue
                  ? "border-red-300 bg-red-50 dark:bg-red-900/20 shadow-red-100 dark:shadow-red-900/20"
                  : ""
              }`}
            >
              <CardContent className="p-0">
                {/* Overdue Warning */}
                {isOverdue && (
                  <div className="p-2 bg-red-100 dark:bg-red-900/30 border-b border-red-300 dark:border-red-700">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-red-800 dark:text-red-200 font-semibold text-xs">
                        ⚠️ {o.card.overdueWarning}
                      </span>
                    </div>
                    <p className="text-red-700 dark:text-red-300 text-xs mt-1">
                      {o.card.overdueMessage}
                    </p>
                  </div>
                )}

                {/* Main Content: Image left, Info right */}
                <div className="flex flex-col md:flex-row">
                  {/* Image - Left */}
                  {listing.thumbnail && (
                    <div className="relative flex-shrink-0 h-40 md:h-auto w-full md:w-2/5 overflow-hidden">
                      <NativeImage
                        src={listing.thumbnail}
                        alt={`${listing.vehicleDetails?.make} ${listing.vehicleDetails?.model}`}
                        className="w-full h-full object-cover"
                        style={{
                          minHeight: "100%",
                          maxHeight: "160px",
                        }}
                      />
                    </div>
                  )}

                  {/* Info - Right */}
                  <div className="flex-1 p-3 md:p-3 lg:p-4 flex flex-col">
                    {/* Mobile Layout */}
                    <div className="md:hidden flex flex-col gap-1.5">
                      {/* Title with button */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 space-y-1">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {listing.title}
                          </h3>
                          {signatureStatusInfo.requiresAction && (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] w-fit">
                              {o.card.signatureRequired ?? "Signering kravs"}
                            </Badge>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs gap-1 flex-shrink-0 h-7 px-2"
                          onClick={(e) =>
                            handleNavigateToListing(listing.id, e)
                          }
                        >
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {listing.vehicleDetails?.make || o.card.unknown}{" "}
                        {listing.vehicleDetails?.model || o.card.unknown}{" "}
                        {listing.vehicleDetails?.year || 0}
                      </p>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {listing.vehicleDetails?.regNumber && (
                          <LicensePlate
                            lpn={listing.vehicleDetails.regNumber}
                          />
                        )}
                        <SaleTypeBadge
                          saleType={listing.listingType === "auction" ? "auction" : "direct-purchase"}
                        />
                      </div>

                      {/* 2x2 Grid for Status, Betalning, Leverans, Signering */}
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Status:
                          </span>
                          <Badge
                            variant="default"
                            className= {listing.status === "completed" ? "bg-green-100 text-green-800 border-green-200 text-xs w-fit" : "bg-orange-100 text-orange-800 border-orange-200 text-xs w-fit"}
                          >
                            {status}
                          </Badge>
                        </div>

                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {o.labels.payment}
                          </span>
                          <Badge
                            variant="outline"
                            className={`${paymentStatusInfo.color} text-xs w-fit`}
                          >
                            {paymentStatusInfo.text}
                          </Badge>
                        </div>

                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {o.labels.delivery}
                          </span>
                          <Badge
                            variant="outline"
                            className={`${
                              transport?.status === "completed"
                                ? "bg-green-100 text-green-800 border-green-200"
                                : transport?.status === "planned" || transport?.status === "in_progress"
                                ? "bg-orange-100 text-orange-800 border-orange-200"
                                : transport?.status === "requested"
                                ? "bg-blue-100 text-blue-800 border-blue-200"
                                : "bg-red-100 text-red-800 border-red-200"
                            } text-xs w-fit`}
                          >
                            {deliveryStatus || o.card.unknown}
                          </Badge>
                        </div>

                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {o.labels.signature ?? o.contractPopup?.signatureStatus ?? "Signeringsstatus"}
                          </span>
                          <Badge
                            variant="outline"
                            className={`${signatureStatusInfo.color} text-xs w-fit`}
                          >
                            {signatureStatusInfo.text}
                          </Badge>
                          {signatureStatus === "signed" && signedByName && (
                            <span className="text-[11px] text-muted-foreground">
                              {o.contractPopup?.signatureSignedBy ?? "Signerad av"}: {signedByName}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {o.card.updated}
                        </span>
                        <span className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">
                          {updatedLabel}
                        </span>
                      </div>

                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden md:grid md:grid-cols-2 gap-2 md:gap-3 lg:gap-4">
                      {/* Left Column */}
                      <div className="flex flex-col gap-1 md:gap-1.5">
                        <h3 className="text-sm md:text-base lg:text-base font-semibold text-gray-900 dark:text-gray-100">
                          {listing.title}
                        </h3>
                        <p className="text-xs md:text-sm lg:text-sm text-gray-600 dark:text-gray-400">
                          {listing.vehicleDetails?.make || o.card.unknown}{" "}
                          {listing.vehicleDetails?.model || o.card.unknown}{" "}
                          {listing.vehicleDetails?.year || 0}
                        </p>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {listing.vehicleDetails?.regNumber && (
                            <LicensePlate
                              lpn={listing.vehicleDetails.regNumber}
                            />
                          )}
                          <SaleTypeBadge
                            saleType={
                              listing.listingType === "auction" ? "auction" : "direct-purchase"
                            }
                          />
                          {signatureStatusInfo.requiresAction && (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
                              <FileSignature className="mr-1 h-3.5 w-3.5" />
                              {o.card.signatureRequired ?? "Signering kravs"}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Right Column */}
                      <div className="flex flex-col gap-1.5">
                        {/* Button at top right */}
                        <div className="flex justify-end mb-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs gap-1 flex-shrink-0 h-7 px-2"
                            onClick={(e) =>
                              handleNavigateToListing(listing.id, e)
                            }
                          >
                            <ExternalLink className="w-3 h-3" />
                            <span>{o.labels.goToListing}</span>
                          </Button>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Status:
                          </span>
                          <Badge
                            variant="default"
                            className={listing.status === "completed" ? "bg-green-100 text-green-800 border-green-200 text-xs" : "bg-orange-100 text-orange-800 border-orange-200 text-xs"}
                          >
                            {status}
                          </Badge>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {o.labels.payment}
                          </span>
                          <Badge
                            variant="outline"
                            className={`${paymentStatusInfo.color} text-xs`}
                          >
                            {paymentStatusInfo.text}
                          </Badge>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {o.labels.delivery}
                          </span>
                          <Badge
                            variant="outline"
                            className={`${
                              transport?.status === "completed"
                                ? "bg-green-100 text-green-800 border-green-200"
                                : transport?.status === "planned" || transport?.status === "in_progress"
                                ? "bg-orange-100 text-orange-800 border-orange-200"
                                : transport?.status === "requested"
                                ? "bg-blue-100 text-blue-800 border-blue-200"
                                : "bg-red-100 text-red-800 border-red-200"
                            } text-xs`}
                          >
                            {deliveryStatus || o.card?.unknown || o.deliveryStatus?.unknown}
                          </Badge>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {o.labels.signature ?? o.contractPopup?.signatureStatus ?? "Signeringsstatus"}
                          </span>
                          <div className="flex flex-col items-end gap-1">
                            <Badge
                              variant="outline"
                              className={`${signatureStatusInfo.color} text-xs`}
                            >
                              {signatureStatusInfo.text}
                            </Badge>
                            {signatureStatus === "signed" && signedByName && (
                              <span className="text-[11px] text-muted-foreground text-right">
                                {o.contractPopup?.signatureSignedBy ?? "Signerad av"}: {signedByName}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {o.card.updated}
                          </span>
                          <span className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">
                            {updatedLabel}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-4 border-t pt-3">
                      {isCompletedOrder ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCardClick(listing.id)}
                        className="flex items-center gap-2 text-xs h-7 px-0"
                      >
                        {selectedCar === listing.id ? (
                          <>
                            <ChevronUp className="h-3 w-3 md:h-4 md:w-4" />
                            <span className="hidden md:inline">{o.card.deliveryInfoHide}</span>
                            <span className="md:hidden">{o.card.deliveryInfoHideShort}</span>
                          </>
                        ) : (
                          <>
                            <Truck className="h-3 w-3 md:h-4 md:w-4" />
                            <span className="hidden md:inline">{o.card.deliveryInfoShow}</span>
                            <span className="md:hidden">{o.card.deliveryInfoShowShort}</span>
                          </>
                        )}
                      </Button>
                      ) : (
                        <span />
                      )}

                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-green-500 text-xs md:text-sm lg:text-base">
                          {price}
                        </span>
                        {hasBidFee && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Info className="h-4 w-4" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 p-4" align="end">
                              <div className="space-y-2 text-sm">
                                <div className="font-semibold mb-3 text-base">
                                  {o.labels.priceBreakdown}
                                </div>
                                <div className="flex justify-between gap-4">
                                  <span className="text-gray-600 dark:text-gray-400">{o.labels.salesPrice}</span>
                                  <span>{basePrice.toLocaleString("sv-SE")} kr</span>
                                </div>
                                <div className="flex justify-between gap-4">
                                  <span className="text-gray-600 dark:text-gray-400">{o.labels.bidFee}</span>
                                  <span>{bidFee.toLocaleString("sv-SE")} kr</span>
                                </div>
                                <div className="flex justify-between gap-4 pt-2 border-t border-gray-200 dark:border-gray-700 font-semibold">
                                  <span>{o.labels.total}</span>
                                  <span className="text-green-600 dark:text-green-400">{price}</span>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>

              {isCompletedOrder && selectedCar === listing.id && (
                <div className="px-3 md:px-4 pb-3 md:pb-4">
                  <CarDetailedInfo
                    listing={listing}
                    payment={payment}
                    transport={transport}
                  />
                </div>
              )}
            </Card>
          </div>
        );
      })}
    </div>
  );
};
